import {
  animationFrameAt,
  tileAnimations,
  type TileAnimation,
  type TiledMap,
} from '@veriverse/map-format';
import type Phaser from 'phaser';

interface AnimatedGroup {
  readonly animation: TileAnimation;
  readonly tiles: Phaser.Tilemaps.Tile[];
  shown: number;
}

/**
 * Plays the map's Tiled tile animations (ADR-0028). Phaser reads the animation data but does not
 * play it, so each tick this swaps the index of every placed animated tile to the current frame.
 * Tiles are grouped by animation and only touched when the frame changes. With reduce motion on,
 * every tile holds its first frame, as looping idle animations do (style guide §5.1).
 */
export class AnimatedTiles {
  private readonly groups: AnimatedGroup[] = [];

  constructor(
    map: TiledMap,
    layers: readonly (Phaser.Tilemaps.TilemapLayer | Phaser.Tilemaps.TilemapGPULayer)[],
    private readonly reducedMotion: boolean,
  ) {
    const animations = tileAnimations(map);
    if (animations.size === 0) return;
    const byGid = new Map<number, AnimatedGroup>();
    for (const layer of layers) {
      layer.forEachTile((tile) => {
        const animation = animations.get(tile.index);
        if (!animation) return;
        let group = byGid.get(tile.index);
        if (!group) {
          group = { animation, tiles: [], shown: -1 };
          byGid.set(tile.index, group);
          this.groups.push(group);
        }
        group.tiles.push(tile);
      });
    }
    this.update(0);
  }

  /** Number of placed tiles that animate; for tests and debugging. */
  get tileCount(): number {
    return this.groups.reduce((sum, group) => sum + group.tiles.length, 0);
  }

  update(time: number): void {
    for (const group of this.groups) {
      const gid = this.reducedMotion
        ? (group.animation.frames[0]?.gid ?? group.animation.gid)
        : animationFrameAt(group.animation, time);
      if (gid === group.shown) continue;
      group.shown = gid;
      for (const tile of group.tiles) tile.index = gid;
    }
  }
}
